import { createSlice } from '@reduxjs/toolkit';
import { showMessage } from 'app/store/fuse/messageSlice';
import UserLoginService from 'app/admin/user/service/UserLoginService';
import { getUserData } from './userSlice';

const STEP = {
  LOGGED_IN: 'loggedIn',
  CHOOSE_GROUP: 'chooseGroup',
  CHOOSE_ROLE: 'chooseRole',
  NO_GROUP: 'noGroup',
  NO_ROLE: 'noRole',
};

const isStepSuccess = (result) => {
  if (!result) return false;
  if (typeof result.lastStepSuccessful === 'boolean') return result.lastStepSuccessful;
  if (typeof result.successful === 'boolean') return result.successful;
  return true;
};

const getNextStep = (result) => (result && result.nextStep != null ? String(result.nextStep) : null);

const getLoginState = () => {
  if (UserLoginService.getLoginState) return UserLoginService.getLoginState();
  return Promise.resolve(null);
};

const stepPayload = (result, nextStep) => ({
  nextStep,
  meta: {
    ...(result?.meta ?? {}),
    groups: result?.userGroups ?? result?.groups ?? result?.meta?.groups ?? result?.client?.userGroups ?? null,
    roles: result?.roles ?? result?.meta?.roles ?? result?.client?.roles ?? null,
    userGroupId: result?.userGroupId ?? result?.groupId ?? result?.meta?.userGroupId ?? result?.meta?.groupId ?? null,
    role: result?.role ?? result?.meta?.role ?? null,
  },
  client: result?.client ?? null,
});

const finalizeLoggedIn = async (dispatch, result) => {
  dispatch(loginSuccess({ client: result?.client ?? null }));
  await dispatch(getUserData());
};

const handleStep = (dispatch, step, result) => {
  if (!step || step === STEP.LOGGED_IN) return false;

  if (step === STEP.NO_GROUP) {
    dispatch(loginNextStep(stepPayload(result, step)));
    dispatch(showMessage({ message: 'No group assigned. Please contact an administrator.' }));
    return true;
  }

  if (step === STEP.NO_ROLE) {
    dispatch(loginNextStep(stepPayload(result, step)));
    dispatch(showMessage({ message: 'No role assigned. Please contact an administrator.' }));
    return true;
  }

  if (step === STEP.CHOOSE_GROUP || step === STEP.CHOOSE_ROLE) {
    dispatch(loginNextStep(stepPayload(result, step)));
    return true;
  }

  dispatch(loginNextStep(stepPayload(result, step)));
  dispatch(showMessage({ message: `Unknown login step "${String(step)}". Please try again.` }));
  return true;
};

const handleResult = async (dispatch, result, { onFail, failToast } = {}) => {
  const step = getNextStep(result);

  if (handleStep(dispatch, step, result)) return true;

  const loggedIn = result === true || result?.isLoggedIn === true || step === STEP.LOGGED_IN;
  if (loggedIn) {
    await finalizeLoggedIn(dispatch, result);
    return true;
  }

  if (!isStepSuccess(result)) {
    onFail?.();
    if (failToast) dispatch(showMessage({ message: failToast }));
    return true;
  }

  return false;
};

const hasAnyKnownSignal = (result) => {
  if (!result) return false;
  const step = getNextStep(result);
  return result === true || result?.isLoggedIn === true || step != null || result?.client != null || result?.meta != null;
};

export const checkLoginState = () => async (dispatch) => {
  dispatch(setBusy());

  try {
    const result = await getLoginState();

    if (!hasAnyKnownSignal(result)) {
      dispatch(resetLoginState());
      return;
    }

    const handled = await handleResult(dispatch, result);
    if (!handled) dispatch(resetLoginState());
  } catch (e) {
    dispatch(resetLoginState());
  }
};

export const submitLogin = ({ email, password }) => async (dispatch) => {
  dispatch(loginStart());

  try {
    const result = await UserLoginService.login(email, password);
    const handled = await handleResult(dispatch, result, {
      onFail: () => dispatch(loginError([{ type: 'email', message: 'Invalid email or password!' }])),
      failToast: 'Login failed. Please try again.',
    });

    if (handled) return;

    dispatch(loginError([{ type: 'server', message: 'Unexpected login state.' }]));
    dispatch(showMessage({ message: 'Unexpected login state. Please try again.' }));
  } catch (e) {
    dispatch(loginError([{ type: 'server', message: 'Network error occurred.' }]));
    dispatch(showMessage({ message: 'Network error. Please try again.' }));
  }
};

export const submitGroupChoice = (groupId) => async (dispatch) => {
  dispatch(setBusy());

  try {
    const result = await UserLoginService.setUserGroupId(groupId);
    const handled = await handleResult(dispatch, result, {
      onFail: () => dispatch(loginStepError([{ type: 'server', message: 'Group selection failed.' }])),
      failToast: 'Group selection failed. Please try again.',
    });

    if (handled) return;

    dispatch(loginSuccess({ client: result?.client ?? null }));
    dispatch(getUserData());
  } catch (e) {
    dispatch(loginStepError([{ type: 'server', message: 'Network error occurred.' }]));
    dispatch(showMessage({ message: 'Network error. Please try again.' }));
  }
};

export const submitRoleChoice = (role) => async (dispatch) => {
  dispatch(setBusy());

  try {
    const result = await UserLoginService.setRole(role);
    const handled = await handleResult(dispatch, result, {
      onFail: () => dispatch(loginStepError([{ type: 'server', message: 'Role selection failed.' }])),
      failToast: 'Role selection failed. Please try again.',
    });

    if (handled) return;

    dispatch(loginSuccess({ client: result?.client ?? null }));
    dispatch(getUserData());
  } catch (e) {
    dispatch(loginStepError([{ type: 'server', message: 'Network error occurred.' }]));
    dispatch(showMessage({ message: 'Network error. Please try again.' }));
  }
};

const initialState = {
  success: false,
  errors: [],
  inProgress: false,
  isLoggedIn: false,
  nextStep: null,
  meta: null,
  client: null,
};

const loginSlice = createSlice({
  name: 'auth/login',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.inProgress = true;
      state.errors = [];
      state.nextStep = null;
      state.meta = null;
      state.client = null;
      state.success = false;
      state.isLoggedIn = false;
    },
    setBusy: (state) => {
      state.inProgress = true;
    },
    loginSuccess: (state, action) => {
      state.inProgress = false;
      state.success = true;
      state.isLoggedIn = true;
      state.errors = [];
      state.nextStep = null;
      state.meta = null;
      state.client = action?.payload?.client ?? null;
    },
    loginNextStep: (state, action) => {
      const { nextStep, meta = null, client = null } = action.payload || {};
      state.inProgress = false;
      state.success = false;
      state.isLoggedIn = false;
      state.errors = [];
      state.nextStep = nextStep || null;
      state.meta = meta;
      state.client = client;
    },
    loginError: (state, action) => {
      state.inProgress = false;
      state.success = false;
      state.isLoggedIn = false;
      state.errors = Array.isArray(action.payload) ? action.payload : [];
      state.nextStep = null;
      state.meta = null;
      state.client = null;
    },
    loginStepError: (state, action) => {
      state.inProgress = false;
      state.success = false;
      state.isLoggedIn = false;
      state.errors = Array.isArray(action.payload) ? action.payload : [];
    },
    resetLoginState: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: {},
});

export const {
  loginStart,
  setBusy,
  loginSuccess,
  loginNextStep,
  loginError,
  loginStepError,
  resetLoginState,
} = loginSlice.actions;

export default loginSlice.reducer;
